// Pulse / AI Visibility section — UAVS score + top remediation items.

import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { Link as RouterLink } from 'react-router-dom';
import SectionShell from './SectionShell';

const SEVERITY_COLOR = {
  high: 'error',
  medium: 'warning',
  low: 'info',
};

export default function VisibilitySection({ visibility }) {
  if (!visibility) {
    return (
      <SectionShell
        title="AI Visibility"
        subtitle="UpRyzze AI Visibility Score"
        icon={<InsightsOutlinedIcon />}
        empty
        emptyCopy="Run your first AI Visibility scan to see your score."
      />
    );
  }

  const scorePct = Math.min(100, Math.max(0, visibility.score));
  const ringColor =
    scorePct >= 75
      ? 'success.main'
      : scorePct >= 50
        ? 'warning.main'
        : 'error.main';

  // SVG ring: 36 viewBox, stroke-dashoffset technique for the score arc.
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - scorePct / 100);

  return (
    <SectionShell
      title="AI Visibility"
      subtitle="How AI search engines see you"
      icon={<InsightsOutlinedIcon />}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 96,
            height: 96,
            flexShrink: 0,
          }}
        >
          <svg
            width="96"
            height="96"
            viewBox="0 0 36 36"
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.12"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: ringColor,
            }}
          >
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                color: 'text.primary',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {scorePct}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: 10 }}
            >
              / 100
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {visibility.deltaPoints != null && visibility.deltaPoints !== 0 ? (
            <Chip
              size="small"
              icon={<ArrowUpwardRoundedIcon />}
              label={`${visibility.deltaPoints > 0 ? '+' : ''}${visibility.deltaPoints} pts vs last scan`}
              color={visibility.deltaPoints > 0 ? 'success' : 'error'}
              sx={{ fontWeight: 600, mb: 0.5 }}
            />
          ) : null}
          {visibility.lastScanAt ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5, fontSize: 13 }}
            >
              Last scanned{' '}
              {new Date(visibility.lastScanAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {(visibility.topIssues || []).length > 0 ? (
        <>
          <Divider />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                display: 'block',
                mb: 1,
              }}
            >
              Top fixes
            </Typography>
            <Stack spacing={1}>
              {visibility.topIssues.slice(0, 3).map((issue) => (
                <Box
                  key={issue.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Chip
                    label={issue.severity}
                    color={SEVERITY_COLOR[issue.severity] || 'default'}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.primary', fontSize: 13 }}
                  >
                    {issue.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </>
      ) : null}

      <Box
        component={RouterLink}
        to="../uavs"
        sx={{
          alignSelf: 'flex-start',
          mt: 'auto',
          color: 'primary.main',
          fontSize: 13,
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        See full report →
      </Box>
    </SectionShell>
  );
}
